import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  chatWebSocketUrl,
  dashboardLayoutWebSocketUrl,
  ensureProjectChatChannel,
  fetchChatMessages,
  getRealtimeAuthToken,
  presenceWebSocketUrl,
  sendChatMessage,
  signalChatTyping,
  type ThreadChatChannel,
  type ThreadChatMessage,
  type ThreadDashboardEditingEvent,
  type ThreadDashboardLayoutOperationEvent,
  type ThreadDashboardLayoutOutboundMessage,
  type ThreadDashboardLayoutSnapshot,
  type ThreadDashboardLayoutStreamEvent,
  type ThreadPresenceState,
  type ThreadRealtimeErrorEvent,
  type ThreadRealtimeStatus,
  type ThreadTypingEvent,
} from "../../api/realtime";
import type { ThreadV4LayoutJson, ThreadV4WidgetPosition, ThreadV4WidgetSpec } from "../../api/threadV4Views";
import { authClient } from "../../lib/auth";

const PRESENCE_HEARTBEAT_MS = 5000;
const WS_RECONNECT_MS = 3000;
const TYPING_REFRESH_MS = 2000;
const LAYOUT_FLUSH_MS = 300;

type PresenceEvent =
  | { type: "snapshot"; states?: unknown[] }
  | { type: "join" | "update"; state?: unknown }
  | { type: "leave"; user_id?: unknown };

type ChatStreamEvent =
  | { type: "message.created" | "message.edited" | "message.deleted"; message?: unknown }
  | ThreadTypingEvent
  | { type: "ping"; ts?: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object";
}

function isPresenceState(value: unknown): value is ThreadPresenceState {
  return isRecord(value) && typeof value.user_id === "string";
}

function isChatMessage(value: unknown): value is ThreadChatMessage {
  return isRecord(value) && typeof value.id === "string" && typeof value.channel_id === "string" && typeof value.body === "string";
}

function isDashboardLayout(value: unknown): value is ThreadV4LayoutJson {
  return isRecord(value) && Array.isArray(value.widgets);
}

function isLayoutSnapshot(value: unknown): value is ThreadDashboardLayoutSnapshot {
  return isRecord(value) && value.type === "snapshot" && typeof value.view_id === "string" && isDashboardLayout(value.layout_json);
}

function isLayoutOperationEvent(value: unknown): value is ThreadDashboardLayoutOperationEvent {
  return isRecord(value)
    && typeof value.type === "string"
    && value.type.startsWith("layout.")
    && typeof value.view_id === "string"
    && typeof value.author_id === "string"
    && isDashboardLayout(value.layout_json);
}

function isDashboardEditingEvent(value: unknown): value is ThreadDashboardEditingEvent {
  return isRecord(value) && value.type === "editing" && typeof value.view_id === "string" && typeof value.user_id === "string" && typeof value.started_at === "string";
}

function isRealtimeErrorEvent(value: unknown): value is ThreadRealtimeErrorEvent {
  return isRecord(value) && value.type === "error";
}

function isTypingEvent(value: unknown): value is ThreadTypingEvent {
  return isRecord(value) && value.type === "typing" && typeof value.user_id === "string" && typeof value.channel_id === "string" && typeof value.expires_at === "string";
}

function parseJsonEvent<T>(data: string): T | null {
  try {
    return JSON.parse(data) as T;
  } catch {
    return null;
  }
}

function upsertPresence(current: ThreadPresenceState[], next: ThreadPresenceState): ThreadPresenceState[] {
  const rest = current.filter((state) => state.user_id !== next.user_id);
  return [...rest, next].sort((left, right) => String(right.last_seen ?? "").localeCompare(String(left.last_seen ?? "")));
}

function upsertMessage(current: ThreadChatMessage[], next: ThreadChatMessage): ThreadChatMessage[] {
  const rest = current.filter((message) => message.id !== next.id);
  return [...rest, next].sort((left, right) => Date.parse(left.created_at) - Date.parse(right.created_at));
}

async function readCurrentUserId(): Promise<string | null> {
  const {
    data: { session },
  } = await authClient.auth.getSession();
  return session?.user?.id ?? null;
}

export function useThreadPresence({
  projectId,
  enabled,
  viewId,
  selection,
}: {
  projectId: string;
  enabled: boolean;
  viewId: string;
  selection: Record<string, unknown> | null;
}): {
  status: ThreadRealtimeStatus;
  peers: ThreadPresenceState[];
  currentUserId: string | null;
} {
  const [status, setStatus] = useState<ThreadRealtimeStatus>("idle");
  const [states, setStates] = useState<ThreadPresenceState[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const viewIdRef = useRef(viewId);
  const selectionRef = useRef(selection);

  useEffect(() => {
    viewIdRef.current = viewId;
    selectionRef.current = selection;
  }, [selection, viewId]);

  useEffect(() => {
    if (!enabled) {
      setStatus("idle");
      setStates([]);
      return;
    }

    let stopped = false;
    let websocket: WebSocket | null = null;
    let heartbeatId: number | null = null;
    let reconnectId: number | null = null;

    const clearTimers = () => {
      if (heartbeatId !== null) window.clearInterval(heartbeatId);
      if (reconnectId !== null) window.clearTimeout(reconnectId);
      heartbeatId = null;
      reconnectId = null;
    };

    const heartbeat = () => {
      if (!websocket || websocket.readyState !== WebSocket.OPEN) return;
      websocket.send(JSON.stringify({
        type: "heartbeat",
        view_id: viewIdRef.current,
        selection: selectionRef.current,
      }));
    };

    const connect = async () => {
      clearTimers();
      setStatus("connecting");
      const token = await getRealtimeAuthToken();
      const userId = await readCurrentUserId();
      if (stopped) return;
      setCurrentUserId(userId);
      if (!token) {
        setStatus("auth-missing");
        return;
      }

      websocket = new WebSocket(presenceWebSocketUrl(projectId, token));
      websocket.onopen = () => {
        if (stopped) return;
        setStatus("connected");
        heartbeat();
        heartbeatId = window.setInterval(heartbeat, PRESENCE_HEARTBEAT_MS);
      };
      websocket.onmessage = (event) => {
        const payload = parseJsonEvent<PresenceEvent>(String(event.data));
        if (!payload) return;
        if (payload.type === "snapshot") {
          const snapshot = (payload.states ?? []).filter(isPresenceState);
          setStates(snapshot);
        } else if ((payload.type === "join" || payload.type === "update") && isPresenceState(payload.state)) {
          const nextState = payload.state;
          setStates((current) => upsertPresence(current, nextState));
        } else if (payload.type === "leave" && typeof payload.user_id === "string") {
          setStates((current) => current.filter((state) => state.user_id !== payload.user_id));
        }
      };
      websocket.onerror = () => {
        if (!stopped) setStatus("error");
      };
      websocket.onclose = (event) => {
        clearTimers();
        if (stopped) return;
        if (event.code === 1008) {
          setStatus("auth-missing");
          return;
        }
        setStatus("error");
        reconnectId = window.setTimeout(connect, WS_RECONNECT_MS);
      };
    };

    void connect();

    return () => {
      stopped = true;
      clearTimers();
      if (websocket && websocket.readyState === WebSocket.OPEN) {
        websocket.send(JSON.stringify({ type: "leave" }));
      }
      websocket?.close(1000, "bye");
    };
  }, [enabled, projectId]);

  const peers = useMemo(
    () => states.filter((state) => state.user_id !== currentUserId),
    [currentUserId, states],
  );

  return { status, peers, currentUserId };
}

export function useDashboardLayoutCollaboration({
  viewId,
  enabled,
  onRemoteLayout,
  onLocalLayoutPersisted,
}: {
  viewId: string | null;
  enabled: boolean;
  onRemoteLayout: (layout: ThreadV4LayoutJson, event: ThreadDashboardLayoutSnapshot | ThreadDashboardLayoutOperationEvent) => void;
  onLocalLayoutPersisted?: (layout: ThreadV4LayoutJson, event: ThreadDashboardLayoutOperationEvent) => void;
}): {
  status: ThreadRealtimeStatus;
  access: "read" | "write" | null;
  documentId: string | null;
  revision: string | null;
  editingUsers: ThreadDashboardEditingEvent[];
  error: string | null;
  publishLayout: (layout: ThreadV4LayoutJson) => void;
  upsertWidget: (widget: ThreadV4WidgetSpec) => void;
  updateWidgetPosition: (widgetId: string, position: ThreadV4WidgetPosition, mode: "move" | "resize") => void;
  removeWidget: (widgetId: string) => void;
  signalEditing: (widgetId: string, mode: "move" | "resize") => void;
} {
  const [status, setStatus] = useState<ThreadRealtimeStatus>("idle");
  const [access, setAccess] = useState<"read" | "write" | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [revision, setRevision] = useState<string | null>(null);
  const [editingUsers, setEditingUsers] = useState<ThreadDashboardEditingEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const websocketRef = useRef<WebSocket | null>(null);
  const flushTimerRef = useRef<number | null>(null);
  const pendingMessageRef = useRef<ThreadDashboardLayoutOutboundMessage | null>(null);
  const localOpIdsRef = useRef<Set<string>>(new Set());
  const currentUserIdRef = useRef<string | null>(null);
  const onRemoteLayoutRef = useRef(onRemoteLayout);
  const onLocalLayoutPersistedRef = useRef(onLocalLayoutPersisted);

  useEffect(() => {
    onRemoteLayoutRef.current = onRemoteLayout;
    onLocalLayoutPersistedRef.current = onLocalLayoutPersisted;
  }, [onLocalLayoutPersisted, onRemoteLayout]);

  const sendNow = useCallback((message: ThreadDashboardLayoutOutboundMessage) => {
    const websocket = websocketRef.current;
    if (!websocket || websocket.readyState !== WebSocket.OPEN) return false;
    const opId = typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `layout-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localOpIdsRef.current.add(opId);
    websocket.send(JSON.stringify({ ...message, op_id: opId }));
    return true;
  }, []);

  const flushPendingMessage = useCallback(() => {
    flushTimerRef.current = null;
    const message = pendingMessageRef.current;
    pendingMessageRef.current = null;
    if (message && !sendNow(message)) {
      pendingMessageRef.current = message;
    }
  }, [sendNow]);

  const sendDebounced = useCallback((message: ThreadDashboardLayoutOutboundMessage) => {
    pendingMessageRef.current = message;
    if (flushTimerRef.current !== null) window.clearTimeout(flushTimerRef.current);
    flushTimerRef.current = window.setTimeout(flushPendingMessage, LAYOUT_FLUSH_MS);
  }, [flushPendingMessage]);

  const sendOrQueue = useCallback((message: ThreadDashboardLayoutOutboundMessage) => {
    if (!sendNow(message)) {
      pendingMessageRef.current = message;
    }
  }, [sendNow]);

  const clearPendingMessage = useCallback(() => {
    if (flushTimerRef.current !== null) {
      window.clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    pendingMessageRef.current = null;
  }, []);

  const publishLayout = useCallback((layout: ThreadV4LayoutJson) => {
    sendDebounced({ type: "layout.replace", layout_json: layout });
  }, [sendDebounced]);

  const upsertWidget = useCallback((widget: ThreadV4WidgetSpec) => {
    clearPendingMessage();
    sendOrQueue({ type: "layout.widget.upsert", widget });
  }, [clearPendingMessage, sendOrQueue]);

  const updateWidgetPosition = useCallback((widgetId: string, position: ThreadV4WidgetPosition, mode: "move" | "resize") => {
    sendDebounced({ type: mode === "resize" ? "layout.widget.resize" : "layout.widget.move", widget_id: widgetId, position });
  }, [sendDebounced]);

  const removeWidget = useCallback((widgetId: string) => {
    clearPendingMessage();
    sendOrQueue({ type: "layout.widget.remove", widget_id: widgetId });
  }, [clearPendingMessage, sendOrQueue]);

  const signalEditing = useCallback((widgetId: string, mode: "move" | "resize") => {
    const websocket = websocketRef.current;
    if (!websocket || websocket.readyState !== WebSocket.OPEN) return;
    websocket.send(JSON.stringify({ type: "editing", widget_id: widgetId, mode }));
  }, []);

  useEffect(() => {
    if (!enabled || !viewId) {
      setStatus("idle");
      setAccess(null);
      setDocumentId(null);
      setRevision(null);
      setEditingUsers([]);
      setError(null);
      clearPendingMessage();
      localOpIdsRef.current.clear();
      return;
    }

    let stopped = false;
    let websocket: WebSocket | null = null;
    let reconnectId: number | null = null;

    const clearTimers = () => {
      if (reconnectId !== null) window.clearTimeout(reconnectId);
      if (flushTimerRef.current !== null) window.clearTimeout(flushTimerRef.current);
      reconnectId = null;
      flushTimerRef.current = null;
    };

    const connect = async () => {
      clearTimers();
      setStatus("connecting");
      setError(null);
      const token = await getRealtimeAuthToken();
      const userId = await readCurrentUserId();
      if (stopped) return;
      currentUserIdRef.current = userId;
      if (!token) {
        setStatus("auth-missing");
        return;
      }

      websocket = new WebSocket(dashboardLayoutWebSocketUrl(viewId, token));
      websocketRef.current = websocket;
      websocket.onopen = () => {
        if (stopped) return;
        setStatus("connected");
        if (pendingMessageRef.current) flushPendingMessage();
      };
      websocket.onmessage = (event) => {
        const payload = parseJsonEvent<ThreadDashboardLayoutStreamEvent>(String(event.data));
        if (!payload || payload.type === "ping" || payload.type === "pong") return;
        if (isLayoutSnapshot(payload)) {
          setAccess(payload.access);
          setDocumentId(payload.document_id);
          setRevision(payload.revision);
          onRemoteLayoutRef.current(payload.layout_json, payload);
        } else if (isLayoutOperationEvent(payload)) {
          setDocumentId(payload.document_id);
          setRevision(payload.revision);
          const isLocalAck = Boolean(payload.op_id && localOpIdsRef.current.has(payload.op_id));
          if (payload.op_id) localOpIdsRef.current.delete(payload.op_id);
          if (isLocalAck || payload.author_id === currentUserIdRef.current) {
            onLocalLayoutPersistedRef.current?.(payload.layout_json, payload);
          } else {
            onRemoteLayoutRef.current(payload.layout_json, payload);
          }
        } else if (isDashboardEditingEvent(payload) && payload.user_id !== currentUserIdRef.current) {
          setEditingUsers((current) => [payload, ...current.filter((item) => item.user_id !== payload.user_id)]);
        } else if (isRealtimeErrorEvent(payload)) {
          setError(typeof payload.detail === "string" ? payload.detail : "Dashboard collaboration error");
        }
      };
      websocket.onerror = () => {
        if (!stopped) setStatus("error");
      };
      websocket.onclose = (event) => {
        if (websocketRef.current === websocket) websocketRef.current = null;
        if (stopped) return;
        if (event.code === 1008) {
          setStatus("auth-missing");
          return;
        }
        setStatus("error");
        reconnectId = window.setTimeout(connect, WS_RECONNECT_MS);
      };
    };

    void connect();

    return () => {
      stopped = true;
      clearTimers();
      websocketRef.current = null;
      websocket?.close(1000, "bye");
    };
  }, [clearPendingMessage, enabled, flushPendingMessage, viewId]);

  useEffect(() => {
    if (!editingUsers.length) return;
    const sweepId = window.setInterval(() => {
      const now = Date.now();
      setEditingUsers((current) => current.filter((event) => {
        const expiresAt = event.expires_at ? Date.parse(event.expires_at) : Date.parse(event.started_at) + 5000;
        return expiresAt > now;
      }));
    }, 1000);
    return () => window.clearInterval(sweepId);
  }, [editingUsers.length]);

  return { status, access, documentId, revision, editingUsers, error, publishLayout, upsertWidget, updateWidgetPosition, removeWidget, signalEditing };
}

export function useProjectChat({
  projectId,
  enabled,
  open,
}: {
  projectId: string;
  enabled: boolean;
  open: boolean;
}): {
  status: ThreadRealtimeStatus | "loading" | "ready";
  streamStatus: ThreadRealtimeStatus;
  channel: ThreadChatChannel | null;
  messages: ThreadChatMessage[];
  typingUsers: ThreadTypingEvent[];
  unreadCount: number;
  sending: boolean;
  error: string | null;
  sendMessage: (body: string) => Promise<boolean>;
  notifyTyping: () => void;
} {
  const [status, setStatus] = useState<ThreadRealtimeStatus | "loading" | "ready">("idle");
  const [streamStatus, setStreamStatus] = useState<ThreadRealtimeStatus>("idle");
  const [channel, setChannel] = useState<ThreadChatChannel | null>(null);
  const [messages, setMessages] = useState<ThreadChatMessage[]>([]);
  const [typingUsers, setTypingUsers] = useState<ThreadTypingEvent[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const openRef = useRef(open);
  const lastTypingSentRef = useRef(0);

  useEffect(() => {
    openRef.current = open;
    if (open) setUnreadCount(0);
  }, [open]);

  useEffect(() => {
    if (!enabled) {
      setStatus("idle");
      setStreamStatus("idle");
      setChannel(null);
      setMessages([]);
      setTypingUsers([]);
      setUnreadCount(0);
      setError(null);
      return;
    }

    let cancelled = false;
    setStatus("loading");
    setError(null);

    void readCurrentUserId().then((userId) => {
      if (!cancelled) setCurrentUserId(userId);
    });

    ensureProjectChatChannel(projectId)
      .then(async (nextChannel) => {
        const nextMessages = await fetchChatMessages(nextChannel.id);
        if (cancelled) return;
        setChannel(nextChannel);
        setMessages(nextMessages);
        setStatus("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus("error");
        setError(err instanceof Error ? err.message : "Project chat is unavailable");
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, projectId]);

  useEffect(() => {
    if (!enabled || !channel) {
      setStreamStatus("idle");
      return;
    }

    let stopped = false;
    let websocket: WebSocket | null = null;
    let reconnectId: number | null = null;

    const connect = async () => {
      if (reconnectId !== null) window.clearTimeout(reconnectId);
      reconnectId = null;
      setStreamStatus("connecting");
      const token = await getRealtimeAuthToken();
      if (stopped) return;
      if (!token) {
        setStreamStatus("auth-missing");
        return;
      }

      websocket = new WebSocket(chatWebSocketUrl(channel.id, token));
      websocket.onopen = () => {
        if (!stopped) setStreamStatus("connected");
      };
      websocket.onmessage = (event) => {
        const payload = parseJsonEvent<ChatStreamEvent>(String(event.data));
        if (!payload || payload.type === "ping") return;
        if ((payload.type === "message.created" || payload.type === "message.edited") && isChatMessage(payload.message)) {
          const nextMessage = payload.message;
          setMessages((current) => upsertMessage(current, nextMessage));
          if (payload.type === "message.created" && !openRef.current && nextMessage.author_id !== currentUserId) {
            setUnreadCount((count) => count + 1);
          }
        } else if (payload.type === "message.deleted" && isChatMessage(payload.message)) {
          const deletedMessageId = payload.message.id;
          setMessages((current) => current.filter((message) => message.id !== deletedMessageId));
        } else if (isTypingEvent(payload) && payload.user_id !== currentUserId) {
          setTypingUsers((current) => [...current.filter((item) => item.user_id !== payload.user_id), payload]);
        }
      };
      websocket.onerror = () => {
        if (!stopped) setStreamStatus("error");
      };
      websocket.onclose = (event) => {
        if (stopped) return;
        if (event.code === 1008) {
          setStreamStatus("auth-missing");
          return;
        }
        setStreamStatus("error");
        reconnectId = window.setTimeout(connect, WS_RECONNECT_MS);
      };
    };

    void connect();

    return () => {
      stopped = true;
      if (reconnectId !== null) window.clearTimeout(reconnectId);
      websocket?.close(1000, "bye");
    };
  }, [channel, currentUserId, enabled]);

  useEffect(() => {
    if (!typingUsers.length) return;
    const sweepId = window.setInterval(() => {
      const now = Date.now();
      setTypingUsers((current) => current.filter((event) => Date.parse(event.expires_at) > now));
    }, 1000);
    return () => window.clearInterval(sweepId);
  }, [typingUsers.length]);

  const sendMessage = useCallback(async (body: string) => {
    const trimmed = body.trim();
    if (!trimmed || !channel || sending) return false;
    setSending(true);
    setError(null);
    try {
      const message = await sendChatMessage(channel.id, trimmed);
      setMessages((current) => upsertMessage(current, message));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Message could not be sent");
      return false;
    } finally {
      setSending(false);
    }
  }, [channel, sending]);

  const notifyTyping = useCallback(() => {
    if (!channel) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current < TYPING_REFRESH_MS) return;
    lastTypingSentRef.current = now;
    void signalChatTyping(channel.id).catch(() => undefined);
  }, [channel]);

  return {
    status,
    streamStatus,
    channel,
    messages,
    typingUsers,
    unreadCount,
    sending,
    error,
    sendMessage,
    notifyTyping,
  };
}
